import { useEffect, useState, lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryCache, type Query } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { toast } from "@/lib/notify";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { supabase, supabaseConfigError } from "@/integrations/supabase/client";
import { ThemeProvider } from "@/components/theme-provider";
import { ModuleLoadBoundary } from "@/components/ModuleLoadBoundary";
import { PageLoader } from "@/components/PageLoader";
import { hideBootstrapLoader } from "@/lib/bootstrapLoader";
import { resetModuleImportRecoveryState } from "@/lib/moduleLoadRecovery";
import { prefetchReferenceCatalogs } from "@/hooks/useReferenceData";

// Deferred: toast UI, layout, push — not needed for first paint
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const AppLayout = lazy(() => import("@/components/AppLayout"));
const PushSubscriptionManager = lazy(() => import("@/components/PushSubscriptionManager").then(m => ({ default: m.PushSubscriptionManager })));

// Lazy-loaded pages — only downloaded when navigated to
const Login = lazy(() => import("@/pages/Login"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Pacientes = lazy(() => import("@/pages/Pacientes"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const Profissionais = lazy(() => import("@/pages/Profissionais"));
const Financeiro = lazy(() => import("@/pages/Financeiro"));
const Prontuario = lazy(() => import("@/pages/Prontuario"));
const ProntuarioHistorico = lazy(() => import("@/pages/ProntuarioHistorico"));
const Estoque = lazy(() => import("@/pages/Estoque"));
const Chat = lazy(() => import("@/pages/Chat"));
const TabelaPrecos = lazy(() => import("@/pages/TabelaPrecos"));
const Configuracoes = lazy(() => import("@/pages/Configuracoes"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Registro = lazy(() => import("@/pages/Registro"));
const Relatorios = lazy(() => import("@/pages/Relatorios"));
const Laudos = lazy(() => import("@/pages/Laudos"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Cadastro = lazy(() => import("@/pages/Cadastro"));
const ConfirmarConsulta = lazy(() => import("@/pages/ConfirmarConsulta"));
const Receituario = lazy(() => import("@/pages/Receituario"));
const PoliticaPrivacidadePublica = lazy(() => import("@/pages/PoliticaPrivacidadePublica"));

const NotFound = lazy(() => import("@/pages/NotFound"));
const Conteudo = lazy(() => import("@/pages/Conteudo"));

const NOTIFICATION_NAVIGATION_MESSAGE = "serafisio-notification-navigate";
const ROUTE_PREFETCH_DELAY_MS = 800;
const ROUTE_PREFETCH_FALLBACK_TIMEOUT_MS = 1200;
const SCROLL_RECOVERY_WATCHDOG_INTERVAL_MS = 1500;
const SCROLL_RECOVERY_WATCHDOG_DURATION_MS = 10000;

const prefetchPacientes = () => import("@/pages/Pacientes");
const prefetchProntuarioHistorico = () => import("@/pages/ProntuarioHistorico");
const prefetchFinanceiro = () => import("@/pages/Financeiro");
const prefetchAppLayout = () => import("@/components/AppLayout");
const prefetchPushManager = () => import("@/components/PushSubscriptionManager");

// Persistable query keys: stable reference catalogs + dashboard snapshots.
// These benefit most from a "last known state" fallback when offline / API is slow.
// Persistable query keys: stable reference catalogs + dashboard/agenda/finance
// snapshots. These benefit most from a "last known state" fallback when offline
// or when the API is slow. Heavy or volatile lists are excluded by size below.
const PERSISTABLE_KEY_PREFIXES = new Set([
  "reference",
  "dashboard",
  "appointment-services",
  "clinic-settings",
  "rooms",
  "services",
  "finance-categories",
  "commissions",
  "exercises",
  "holidays",
  "inactive-patient-settings",
  // Agenda — stable catalogs (packages, mensalidades) used on every open of
  // the new-appointment modal.
  "agenda",
  // Financeiro — yearly INSS snapshots used by the master settings screen.
  "inss-contribution",
  "inss-transfers",
  "inss-despesas-existentes",
  // Pacientes inativos — heat map used by the patients screen.
  "inactive-patients-map",
]);

// Keep individual persisted queries small so localStorage stays under control
// (~5MB hard cap across browsers, shared with other app state).
const PERSIST_QUERY_MAX_BYTES = 200_000; // ~200KB per query

function isPersistableQuery(query: Query): boolean {
  const root = query.queryKey?.[0];
  if (typeof root !== "string") return false;
  if (!PERSISTABLE_KEY_PREFIXES.has(root)) return false;
  // Skip outsized payloads to avoid blowing the localStorage quota.
  try {
    const data = query.state.data;
    if (data === undefined) return false;
    const size = JSON.stringify(data).length;
    return size <= PERSIST_QUERY_MAX_BYTES;
  } catch {
    return false;
  }
}

// Toast budget: avoid spamming the user when many queries fail at once.
let lastCachedFallbackToastAt = 0;
function maybeNotifyCachedFallback() {
  const now = Date.now();
  if (now - lastCachedFallbackToastAt < 30_000) return;
  lastCachedFallbackToastAt = now;
  toast.info("Sem resposta do servidor — exibindo dados em cache.", { duration: 4000 });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      // If we still have cached data, surface a discreet "showing cached" hint
      // instead of letting the screen go blank. Individual screens can also
      // render their own banners using `isError && data`.
      if (query.state.data !== undefined) {
        maybeNotifyCachedFallback();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min — avoid redundant refetches
      gcTime: 1000 * 60 * 60 * 24, // 24h cache retention — supports offline fallback
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
      refetchOnMount: false,    // honor staleTime on mount — instant cache hits when navigating
      retry: 1,
      networkMode: "offlineFirst",
    },
    mutations: {
      networkMode: "offlineFirst",
      retry: 0,
    },
  },
});

const queryPersister = typeof window !== "undefined"
  ? createSyncStoragePersister({
      storage: window.localStorage,
      key: "serafisio-query-cache-v2",
      throttleTime: 1500,
      // Best-effort: if quota is exceeded, drop the persisted snapshot
      // silently so the app keeps working in-memory.
      retry: ({ error }) => {
        if (typeof console !== "undefined") {
          console.warn("[query-cache] persistence retry skipped", error);
        }
        return undefined;
      },
    })
  : undefined;

const persistOptions = {
  persister: queryPersister!,
  maxAge: 1000 * 60 * 60 * 24, // 24h
  buster: "v2",
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { state: { status: string }; queryKey: readonly unknown[] }) =>
      query.state.status === "success" && isPersistableQuery(query as unknown as Query),
  },
} as const;

const prefetchCriticalRoutes = () => {
  void Promise.allSettled([
    prefetchAppLayout(),
    prefetchPushManager(),
    prefetchPacientes(),
    prefetchProntuarioHistorico(),
    prefetchFinanceiro(),
    prefetchReferenceCatalogs(queryClient),
  ]);
};

function AuthStateNavigator() {
  const navigate = useNavigate();


  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; url?: string } | undefined;
      if (payload?.type !== NOTIFICATION_NAVIGATION_MESSAGE || typeof payload.url !== "string") return;

      try {
        const nextUrl = new URL(payload.url, window.location.origin);
        if (nextUrl.origin !== window.location.origin) return;

        const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

        if (nextPath !== currentPath) {
          navigate(nextPath);
        }
      } catch {
        // Ignore malformed payloads from service worker messages.
      }
    };

    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
  }, [navigate]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Defer scroll recovery imports — not needed for first paint
    Promise.all([
      import("@/utils/scrollMonitor"),
      import("@/lib/bodyScrollLock"),
    ]).then(([scrollMod, bodyLockMod]) => {
      const stopScrollMonitor = scrollMod.startScrollMonitor();
      let watchdogIntervalId: number | null = null;
      let watchdogDeadline = 0;

      const stopWatchdog = () => {
        if (watchdogIntervalId === null) return;
        window.clearInterval(watchdogIntervalId);
        watchdogIntervalId = null;
        watchdogDeadline = 0;
      };

      const startWatchdog = () => {
        if (watchdogIntervalId !== null) return;
        watchdogDeadline = Date.now() + SCROLL_RECOVERY_WATCHDOG_DURATION_MS;
        watchdogIntervalId = window.setInterval(() => {
          bodyLockMod.recoverBodyScrollIfStuck();
          if (!bodyLockMod.isBodyScrollRecoveryNeeded() || Date.now() >= watchdogDeadline) {
            stopWatchdog();
          }
        }, SCROLL_RECOVERY_WATCHDOG_INTERVAL_MS);
      };

      const recoverBodyScrollWithWatchdog = () => {
        const hasInconsistency = bodyLockMod.isBodyScrollRecoveryNeeded();
        bodyLockMod.recoverBodyScrollIfStuck();
        if (hasInconsistency) startWatchdog();
      };

      const handlePageShow = () => recoverBodyScrollWithWatchdog();
      const handleFocus = () => recoverBodyScrollWithWatchdog();
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") return;
        recoverBodyScrollWithWatchdog();
      };

      recoverBodyScrollWithWatchdog();
      window.addEventListener("pageshow", handlePageShow);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocus);

      cleanup = () => {
        stopScrollMonitor();
        stopWatchdog();
        window.removeEventListener("pageshow", handlePageShow);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleFocus);
      };
    });

    return () => cleanup?.();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasRecoveryInHash = window.location.hash.includes("type=recovery");
    const search = new URLSearchParams(window.location.search);
    const hasRecoveryInQuery = search.get("type") === "recovery";

    if (!hasRecoveryInHash && !hasRecoveryInQuery) return;
    if (window.location.pathname === "/reset-password") return;

    const queryString = window.location.search || "";
    const hashString = window.location.hash || "";
    navigate(`/reset-password${queryString}${hashString}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        navigate(`/reset-password${hash}`, { replace: true });
      }

      if (event === "SIGNED_OUT") {
        navigate("/login", { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}

function PostLoginRoutePrefetcher() {
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined" || !user) return;

    let cancelled = false;
    let idleRequestId: number | null = null;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const runPrefetch = () => {
      if (cancelled) return;
      prefetchCriticalRoutes();
    };

    const scheduleIdlePrefetch = () => {
      if ("requestIdleCallback" in window) {
        idleRequestId = window.requestIdleCallback(runPrefetch, { timeout: ROUTE_PREFETCH_FALLBACK_TIMEOUT_MS });
      } else {
        fallbackTimeoutId = setTimeout(runPrefetch, ROUTE_PREFETCH_FALLBACK_TIMEOUT_MS);
      }
    };

    const delayTimer = window.setTimeout(scheduleIdlePrefetch, ROUTE_PREFETCH_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(delayTimer);

      if (idleRequestId !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleRequestId);
      }

      if (fallbackTimeoutId !== null) {
        window.clearTimeout(fallbackTimeoutId);
      }
    };
  }, [user]);

  return null;
}

function DeferredSonner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const trigger = () => setShow(true);
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(trigger, { timeout: 2000 });
    } else {
      setTimeout(trigger, 1200);
    }
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <Sonner />
    </Suspense>
  );
}

function AppBootSignal() {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.appMounted = "true";
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("serafisio:app-mounted"));
    }

    hideBootstrapLoader();
  }, []);

  return null;
}

function BootstrapLoaderDismiss() {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.appMounted = "true";
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("serafisio:app-mounted"));
    }
    hideBootstrapLoader();
    // App mounted successfully — clear any module-recovery counter so future
    // legitimate failures get fresh retry budget.
    resetModuleImportRecoveryState();
  }, []);

  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <><BootstrapLoaderDismiss /><AppLayout><PushSubscriptionManager />{children}</AppLayout></>;
}

function MasterRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isMaster } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isMaster) return <Navigate to="/" replace />;
  return <><BootstrapLoaderDismiss /><AppLayout>{children}</AppLayout></>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isPasswordRecovery } = useAuth();
  if (loading) return <PageLoader />;

  // Detect recovery flow from URL before PASSWORD_RECOVERY event fires
  const isRecoveryUrl =
    window.location.pathname === "/reset-password" ||
    window.location.pathname === "/recovery" ||
    window.location.hash.includes("type=recovery") ||
    window.location.search.includes("type=recovery");

  if (user && !isPasswordRecovery && !isRecoveryUrl) return <Navigate to="/" replace />;
  return <main id="main-content"><BootstrapLoaderDismiss />{children}</main>;
}

function PublicMainRoute({ children }: { children: React.ReactNode }) {
  return <main id="main-content"><BootstrapLoaderDismiss />{children}</main>;
}

const App = () => (
  supabaseConfigError ? (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <section className="w-full max-w-xl rounded-lg border border-destructive/30 bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold mb-2">Sistema não carregado</h1>
        <p className="text-sm leading-relaxed">
          Não foi possível iniciar o sistema porque variáveis de ambiente obrigatórias do Supabase não foram definidas.
        </p>
        <p className="mt-3 text-sm font-medium text-destructive">{supabaseConfigError}</p>
        <p className="mt-3 text-xs text-muted-foreground">
          Configure as variáveis no arquivo <code>.env</code> e reinicie o servidor.
        </p>
      </section>
    </main>
  ) : (
  <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="serafisio-theme" themes={["light", "dark"]}>
      <TooltipProvider>
        <DeferredSonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <AppBootSignal />
            <AuthStateNavigator />
            <PostLoginRoutePrefetcher />
            <ModuleLoadBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
                  <Route path="/recovery" element={<AuthRoute><ResetPassword /></AuthRoute>} />
                  <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
                  <Route path="/cadastro" element={<AuthRoute><Cadastro /></AuthRoute>} />
                  <Route path="/" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                  <Route path="/index" element={<Navigate to="/" replace />} />
                  <Route path="/agendamento" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
                  <Route path="/agenda" element={<Navigate to="/agendamento" replace />} />
                  <Route path="/agendamentos" element={<Navigate to="/agendamento" replace />} />
                  <Route path="/pacientes" element={<ProtectedRoute><Pacientes /></ProtectedRoute>} />
                  <Route path="/profissionais" element={<MasterRoute><Profissionais /></MasterRoute>} />
                  <Route path="/financeiro" element={<MasterRoute><Financeiro /></MasterRoute>} />
                  <Route path="/prontuario" element={<ProtectedRoute><ProntuarioHistorico /></ProtectedRoute>} />
                  <Route path="/prontuario/:id" element={<ProtectedRoute><Prontuario /></ProtectedRoute>} />
                  <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                  <Route path="/estoque" element={<MasterRoute><Estoque /></MasterRoute>} />
                  <Route path="/servicos" element={<MasterRoute><Navigate to="/configuracoes" replace /></MasterRoute>} />
                  <Route path="/tabela" element={<MasterRoute><TabelaPrecos /></MasterRoute>} />
                  <Route path="/ajuda" element={<ProtectedRoute><Ajuda /></ProtectedRoute>} />
                  <Route path="/configuracoes" element={<MasterRoute><Configuracoes /></MasterRoute>} />
                  <Route path="/dashboard" element={<MasterRoute><Dashboard /></MasterRoute>} />
                  <Route path="/relatorios" element={<MasterRoute><Relatorios /></MasterRoute>} />
                  <Route path="/registro" element={<MasterRoute><Registro /></MasterRoute>} />
                  <Route path="/laudos" element={<ProtectedRoute><Laudos /></ProtectedRoute>} />
                  <Route path="/receituario" element={<MasterRoute><Receituario /></MasterRoute>} />
                  <Route path="/conteudo" element={<MasterRoute><Conteudo /></MasterRoute>} />
                  <Route path="/confirmar" element={<PublicMainRoute><ConfirmarConsulta /></PublicMainRoute>} />
                  <Route path="/politica-de-privacidade-publica" element={<PublicMainRoute><PoliticaPrivacidadePublica /></PublicMainRoute>} />
                  
                  <Route path="*" element={<PublicMainRoute><NotFound /></PublicMainRoute>} />
                </Routes>
              </Suspense>
            </ModuleLoadBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </PersistQueryClientProvider>
  )
);

export default App;
